<script setup>
/* global axios */
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, computed, markRaw, watch, onMounted, onBeforeUnmount } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import FlowCanvas from './FlowCanvas.vue';
import IdeaNode from './IdeaNode.vue';
import { uid } from './model';
import {
  emptyIdeaMap,
  ideaNode,
  ideaDocument,
  decorateIdeaMap,
  removeIdeaNode,
  ideaKinds,
  ideaError,
} from './idea-model';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
  group: { type: Object, required: true },
});
// Capture the tenant/group: this component is keyed by both in Automation.vue.
const base = `/api/v1/accounts/${props.accountId}/tg_automation/${props.group.id}/idea_maps`;
const formId = uid();
const maps = ref([]);
const nextMap = ref(null);
const mapId = ref('');
const doc = ref(null);
const version = ref(0);
const latest = ref(0);
const historical = ref(false);
const loading = ref(true);
const busy = ref(false);
const dirty = ref(false);
const error = ref('');
const notice = ref('');
const pending = ref(null);
const denied = ref(false);
const selectedNodeId = ref('');
const selectedEdgeId = ref('');
const canvas = ref(null);
const canvasKey = ref(0);
const listMode = ref(false);
const edits = ref([]);
const redos = ref([]);
const nodeTypes = { idea: markRaw(IdeaNode) };
const versions = ref([]);
const nextVersion = ref(null);
const versionsDialog = ref(null);
const createDialog = ref(null);
const exportDialog = ref(null);
const confirmDialog = ref(null);
const confirmText = ref('');
let confirmResolve = null;
function askConfirm(text) {
  if (confirmResolve) return Promise.resolve(false);
  confirmText.value = text;
  return new Promise(resolve => {
    confirmResolve = resolve;
    confirmDialog.value.open();
  });
}
function answerConfirm(answer, close = true) {
  const resolve = confirmResolve;
  confirmResolve = null;
  if (close) confirmDialog.value?.close();
  resolve?.(answer);
}
const newTitle = ref('');
const versionNote = ref('');
const commentText = ref('');
const commentNode = ref('');
const linkFrom = ref('');
const linkTo = ref('');
const linkLabel = ref('');
const selectedNode = computed(() =>
  doc.value?.nodes.find(n => n.id === selectedNodeId.value)
);
const selectedEdge = computed(() =>
  doc.value?.edges.find(e => e.id === selectedEdgeId.value)
);
const editable = computed(
  () =>
    doc.value &&
    !historical.value &&
    !busy.value &&
    !pending.value &&
    !denied.value
);
const hasChanges = computed(
  () => dirty.value || Boolean(commentText.value.trim())
);
const selectClass =
  'm-0 min-h-10 max-w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline focus:outline-1 focus:outline-n-brand';
let alive = true;
const controller = new AbortController();
watch(
  doc,
  () => {
    canvasKey.value += 1;
  },
  { flush: 'sync' }
);
async function request(path = '', method = 'get', data = undefined) {
  return (
    await axios({
      url: base + path,
      method,
      data,
      signal: controller.signal,
      headers: method === 'get' ? {} : { 'Content-Type': 'application/json' },
    })
  ).data;
}
async function canLeave() {
  if (confirmResolve) return false;
  if (busy.value || pending.value) {
    error.value =
      'Сначала дождитесь результата или повторите сохранение карты.';
    return false;
  }
  return (
    !hasChanges.value ||
    askConfirm('У карты есть несохранённые изменения. Перейти без сохранения?')
  );
}
defineExpose({
  canLeave,
  isBusy: computed(() => busy.value || Boolean(pending.value)),
});
function changed() {
  if (editable.value) {
    dirty.value = true;
    notice.value = '';
  }
}
function remember() {
  edits.value.push(JSON.stringify(ideaDocument(doc.value)));
  if (edits.value.length > 30) edits.value.shift();
  redos.value = [];
}
function undo() {
  if (!editable.value || !edits.value.length) return;
  redos.value.push(JSON.stringify(ideaDocument(doc.value)));
  doc.value = decorateIdeaMap(JSON.parse(edits.value.pop()));
  changed();
}
function redo() {
  if (!editable.value || !redos.value.length) return;
  edits.value.push(JSON.stringify(ideaDocument(doc.value)));
  doc.value = decorateIdeaMap(JSON.parse(redos.value.pop()));
  changed();
}
function apply(result) {
  denied.value = false;
  mapId.value = String(result.id);
  doc.value = decorateIdeaMap(result.document);
  version.value = result.version;
  latest.value = result.latest_version || result.version;
  historical.value = Boolean(result.historical);
  dirty.value = false;
  edits.value = [];
  redos.value = [];
  selectedNodeId.value = doc.value.nodes[0]?.id || '';
  selectedEdgeId.value = '';
  commentText.value = '';
  commentNode.value = '';
  linkFrom.value = '';
  linkTo.value = '';
  linkLabel.value = '';
  versionNote.value = '';
  if (!historical.value) {
    const entry = {
      id: result.id,
      title: doc.value.title,
      version: result.version,
      updated_at: result.updated_at,
    };
    const index = maps.value.findIndex(m => m.id === result.id);
    if (index < 0) maps.value.unshift(entry);
    else maps.value[index] = entry;
  }
}
async function load(id = mapId.value, targetVersion = null) {
  if (!id) return;
  loading.value = true;
  error.value = '';
  notice.value = '';
  try {
    const result = await request(
      `/${id}${targetVersion ? `/versions/${targetVersion}` : ''}`
    );
    if (alive) apply(result);
  } catch (e) {
    if (alive) error.value = ideaError(e);
  } finally {
    if (alive) loading.value = false;
  }
}
async function list(more = false) {
  loading.value = true;
  error.value = '';
  try {
    const result = await request(more ? `?before=${nextMap.value}` : '');
    if (!alive) return;
    denied.value = false;
    maps.value = more ? [...maps.value, ...result.items] : result.items;
    nextMap.value = result.next;
    if (!more && result.items.length) await load(String(result.items[0].id));
  } catch (e) {
    if (alive) {
      error.value = ideaError(e);
      denied.value = e.response?.status === 403;
    }
  } finally {
    if (alive) loading.value = false;
  }
}
async function choose(event) {
  if (!(await canLeave())) {
    event.target.value = mapId.value;
    return;
  }
  await load(event.target.value);
}
async function perform() {
  if (!pending.value || busy.value) return false;
  busy.value = true;
  error.value = '';
  try {
    const p = pending.value;
    const result = await request(p.path, p.method, p.body);
    if (!alive) return false;
    pending.value = null;
    apply(result);
    notice.value = `Сохранена версия ${result.committed_version || result.version}. Текущая версия: ${result.version}.`;
    createDialog.value?.close();
    versionsDialog.value?.close();
    return true;
  } catch (e) {
    if (alive) {
      error.value = ideaError(e);
      if (e.response && e.response.status < 500) pending.value = null;
    }
    return false;
  } finally {
    if (alive) busy.value = false;
  }
}
async function repeat() {
  if (!pending.value || busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    const result = await request(`/requests/${pending.value.body.request_key}`);
    if (!alive) return;
    if (result.found) {
      pending.value = null;
      apply(result);
      notice.value = `Подтверждено сохранение версии ${result.committed_version}.`;
      createDialog.value?.close();
      versionsDialog.value?.close();
      return;
    }
  } catch (e) {
    if (alive) error.value = ideaError(e);
    return;
  } finally {
    if (alive) busy.value = false;
  }
  await perform();
}
async function create() {
  if (busy.value || pending.value || !newTitle.value.trim()) return;
  pending.value = {
    method: 'post',
    path: '',
    body: {
      request_key: uid(),
      document: emptyIdeaMap(newTitle.value.trim()),
      note: 'Создание карты',
    },
  };
  await perform();
}
function addComment() {
  if (
    !editable.value ||
    !commentText.value.trim() ||
    doc.value.comments.length >= 200
  )
    return;
  remember();
  doc.value.comments.push({
    id: uid(),
    node_id: commentNode.value || null,
    text: commentText.value.trim(),
    resolved: false,
  });
  commentText.value = '';
  changed();
}

async function save() {
  if (!editable.value) return false;
  if (commentText.value.trim() && doc.value.comments.length >= 200) {
    error.value =
      'В карте уже 200 комментариев. Сократите их количество перед сохранением нового.';
    return false;
  }
  if (commentText.value.trim()) addComment();
  if (!dirty.value) return true;
  pending.value = {
    method: 'put',
    path: `/${mapId.value}`,
    body: {
      request_key: uid(),
      expected_version: version.value,
      document: ideaDocument(doc.value),
      note: versionNote.value,
    },
  };
  return perform();
}
function addNode() {
  if (!editable.value || doc.value.nodes.length >= 100) return;
  remember();
  const node = ideaNode(doc.value.nodes.length);
  doc.value.nodes.push(node);
  selectedNodeId.value = node.id;
  selectedEdgeId.value = '';
  changed();
}
async function removeNode() {
  if (
    !selectedNode.value ||
    !(await askConfirm(
      'Удалить блок, его стрелки и комментарии? Сохранённые версии останутся.'
    ))
  )
    return;
  remember();
  const id = selectedNodeId.value;
  removeIdeaNode(doc.value, id);
  if (commentNode.value === id) commentNode.value = '';
  if (linkFrom.value === id) linkFrom.value = '';
  if (linkTo.value === id) linkTo.value = '';
  selectedNodeId.value = '';
  changed();
}
function connect(connection) {
  if (
    !editable.value ||
    !doc.value.nodes.some(n => n.id === connection.source) ||
    !doc.value.nodes.some(n => n.id === connection.target) ||
    doc.value.edges.length >= 200
  )
    return;
  remember();
  const e = {
    id: uid(),
    source: connection.source,
    target: connection.target,
    sourceHandle: 'yes',
    targetHandle: 'in',
    label: connection.label || '',
    markerEnd: 'arrowclosed',
    data: { kind: 'transition' },
  };
  doc.value.edges.push(e);
  selectedEdgeId.value = e.id;
  selectedNodeId.value = '';
  changed();
}
function removeEdge() {
  if (!selectedEdge.value || !editable.value) return;
  remember();
  doc.value.edges = doc.value.edges.filter(e => e.id !== selectedEdgeId.value);
  selectedEdgeId.value = '';
  changed();
}
async function showVersions(more = false) {
  if (busy.value || pending.value) return;
  busy.value = true;
  error.value = '';
  try {
    const result = await request(
      `/${mapId.value}/versions${more ? `?before=${nextVersion.value}` : ''}`
    );
    if (alive) {
      versions.value = more
        ? [...versions.value, ...result.items]
        : result.items;
      nextVersion.value = result.next;
      versionsDialog.value.open();
    }
  } catch (e) {
    if (alive) error.value = ideaError(e);
  } finally {
    if (alive) busy.value = false;
  }
}
async function viewVersion(value) {
  if (loading.value || !(await canLeave())) return;
  versionsDialog.value.close();
  await load(mapId.value, value);
}
async function restore() {
  if (
    !historical.value ||
    busy.value ||
    pending.value ||
    !(await askConfirm(
      `Создать новую текущую версию из версии ${version.value}? Существующие версии сохранятся.`
    ))
  )
    return;
  pending.value = {
    method: 'put',
    path: `/${mapId.value}`,
    body: {
      request_key: uid(),
      expected_version: latest.value,
      restore_version: version.value,
      note: `Восстановление версии ${version.value}`,
    },
  };
  await perform();
}
async function prepareExport() {
  if (hasChanges.value && !(await save())) return;
  if (!pending.value) exportDialog.value.open();
}
function download(value, filename, type) {
  const url = URL.createObjectURL(new Blob([value], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
async function downloadVersion(kind) {
  busy.value = true;
  error.value = '';
  try {
    const value = await request(
      `/${mapId.value}/versions/${version.value}/export/${kind}`
    );
    if (alive)
      download(
        kind === 'json' ? JSON.stringify(value, null, 2) : value,
        `idea-map-${mapId.value}-v${version.value}.${kind === 'json' ? 'json' : 'md'}`,
        kind === 'json' ? 'application/json' : 'text/markdown'
      );
  } catch (e) {
    if (alive) error.value = ideaError(e);
  } finally {
    if (alive) busy.value = false;
  }
}
function beforeUnload(event) {
  if (hasChanges.value || pending.value || busy.value) {
    event.preventDefault();
    event.returnValue = '';
  }
}
onMounted(() => {
  listMode.value = window.matchMedia('(max-width: 768px)').matches;
  window.addEventListener('beforeunload', beforeUnload);
  list();
});
onBeforeUnmount(() => {
  answerConfirm(false, false);
  alive = false;
  controller.abort();
  window.removeEventListener('beforeunload', beforeUnload);
});
</script>

<template>
  <div class="flex min-w-0 flex-col gap-4">
    <Banner color="blue">
      Карта идей — набросок для обсуждения. Она не запускает сценарии и не
      отправляет сообщения.
    </Banner>
    <Banner v-if="error" color="ruby" role="alert">{{ error }}</Banner>
    <Banner v-if="notice" color="teal" role="status">{{ notice }}</Banner>
    <Banner v-if="pending && !busy" color="amber">
      Ответ сервера не подтверждён. Блоки временно защищены от изменения.
      Повторите ту же операцию, чтобы проверить её результат.<Button
        label="Проверить и повторить сохранение"
        slate
        outline
        class="mt-2"
        @click="repeat"
      />
    </Banner>
    <div class="flex flex-wrap items-end gap-3">
      <label class="flex min-w-0 flex-1 flex-col gap-2 text-sm"
        >Карта<select
          :value="mapId"
          :class="selectClass"
          :disabled="loading || busy || !!pending"
          @change="choose"
        >
          <option v-if="!maps.length" value="">Карт пока нет</option>
          <option v-for="m in maps" :key="m.id" :value="String(m.id)">
            {{ m.title }} · v{{ m.version }}
          </option>
        </select></label
      >
      <Button
        label="Новая карта"
        slate
        outline
        :disabled="loading || busy || !!pending || denied"
        @click="
          async () => {
            if (await canLeave()) {
              newTitle = '';
              createDialog.open();
            }
          }
        "
      />
      <Button
        label="Обновить карту"
        slate
        outline
        :disabled="loading || busy || !!pending"
        @click="
          async () => {
            if (await canLeave()) {
              if (mapId) load();
              else list();
            }
          }
        "
      />
      <Button
        v-if="nextMap"
        label="Ещё карты"
        slate
        ghost
        :disabled="busy || loading"
        @click="list(true)"
      />
    </div>
    <p v-if="loading" role="status" class="text-sm text-n-slate-11">
      Загрузка карты…
    </p>
    <p v-else-if="!doc && !error" class="text-sm text-n-slate-11">
      Создайте карту: добавляйте текстовые блоки, соединяйте стрелками и
      оставляйте вопросы. Незавершённые ветки и циклы допустимы.
    </p>
    <template v-if="doc && !loading">
      <div class="flex flex-wrap items-center gap-3 text-sm">
        <p class="m-0">
          Карта №{{ mapId }} · версия {{ version }}
          <template v-if="historical"> · просмотр сохранённой версии</template>
        </p>
        <p v-if="dirty" class="m-0 text-n-amber-11">
          Есть несохранённые изменения карты
        </p>
        <Button
          label="Версии"
          slate
          outline
          :disabled="busy || !!pending"
          @click="showVersions(false)"
        /><Button
          label="Подготовить для разбора"
          slate
          outline
          :disabled="busy || !!pending"
          @click="prepareExport"
        />
      </div>
      <Banner v-if="historical" color="amber">
        Вы просматриваете неизменяемую версию. Восстановление создаст новую
        версию карты.
        <div class="mt-2 flex flex-wrap gap-2">
          <Button
            label="Восстановить эту версию"
            slate
            outline
            :disabled="busy || !!pending"
            @click="restore"
          /><Button
            label="К текущей версии"
            slate
            ghost
            :disabled="busy || !!pending"
            @click="load(mapId)"
          />
        </div>
      </Banner>
      <fieldset
        :disabled="!editable"
        class="m-0 flex min-w-0 flex-col gap-4 border-0 p-0"
        :class="!editable ? 'pointer-events-none opacity-70' : ''"
        @input="changed"
      >
        <Input v-model="doc.title" label="Название карты" :maxlength="160" />
        <TextArea
          :id="`${formId}-description`"
          v-model="doc.description"
          auto-height
          resize
          show-character-count
          min-height="5rem"
          max-height="24rem"
          label="Цель и описание карты"
          :max-length="6000"
          placeholder="Опиши обычными словами, что должно происходить и какой результат нужен"
        />
        <div class="flex flex-wrap gap-2">
          <Button
            label="Добавить текстовый блок"
            slate
            outline
            :disabled="doc.nodes.length >= 100"
            @click="addNode"
          /><Button
            label="Отменить действие"
            slate
            ghost
            :disabled="!edits.length"
            @click="undo"
          /><Button
            label="Вернуть действие"
            slate
            ghost
            :disabled="!redos.length"
            @click="redo"
          />
        </div>
      </fieldset>
      <div class="flex flex-wrap gap-2">
        <Button
          :label="listMode ? 'Показать схему' : 'Показать список блоков'"
          slate
          outline
          :disabled="busy"
          @click="listMode = !listMode"
        /><Button
          label="Уместить карту"
          slate
          ghost
          :disabled="listMode || busy"
          @click="canvas?.fitView({ padding: 0.15 })"
        />
      </div>
      <div class="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div
          class="min-w-0 rounded-lg border border-n-weak"
          :class="busy || pending ? 'pointer-events-none' : ''"
        >
          <div v-if="listMode" class="flex flex-col gap-2 p-4">
            <p v-if="!doc.nodes.length" class="text-sm text-n-slate-11">
              Блоков пока нет.
            </p>
            <Button
              v-for="n in doc.nodes"
              :key="n.id"
              :label="n.data.title"
              slate
              outline
              class="!justify-start"
              @click="
                selectedNodeId = n.id;
                selectedEdgeId = '';
              "
            />
          </div>
          <FlowCanvas
            v-else
            :key="canvasKey"
            ref="canvas"
            :flow="doc"
            :node-types="nodeTypes"
            :nodes-draggable="!!editable"
            :nodes-connectable="!!editable"
            :edges-updatable="false"
            class="idea-map-canvas"
            @connect="connect"
            @node-click="
              selectedNodeId = $event.node.id;
              selectedEdgeId = '';
            "
            @edge-click="
              selectedEdgeId = $event.edge.id;
              selectedNodeId = '';
            "
            @node-drag-start="remember"
            @node-drag-stop="changed"
          />
        </div>
        <fieldset
          :disabled="!editable"
          class="m-0 min-w-0 rounded-lg border border-n-weak p-4"
          @input="changed"
        >
          <template v-if="selectedNode">
            <div class="mb-4 flex flex-wrap justify-between gap-2">
              <h3 class="text-sm font-medium">Текстовый блок</h3>
              <Button
                label="Удалить блок карты"
                ruby
                ghost
                @click="removeNode"
              />
            </div>
            <div class="flex flex-col gap-4">
              <label class="flex flex-col gap-2 text-sm"
                >Вид блока<select
                  v-model="selectedNode.data.kind"
                  :class="selectClass"
                >
                  <option
                    v-for="(label, value) in ideaKinds"
                    :key="value"
                    :value="value"
                  >
                    {{ label }}
                  </option>
                </select></label
              ><Input
                v-model="selectedNode.data.title"
                label="Заголовок блока"
                :maxlength="160"
              /><TextArea
                :id="`${formId}-block-text`"
                v-model="selectedNode.data.text"
                auto-height
                resize
                show-character-count
                min-height="10rem"
                max-height="24rem"
                label="Описание блока"
                :max-length="6000"
              />
              <p class="break-all text-xs text-n-slate-11">
                ID: {{ selectedNode.id }}
              </p>
            </div>
          </template>
          <template v-else-if="selectedEdge">
            <div class="mb-4 flex flex-wrap justify-between gap-2">
              <h3 class="text-sm font-medium">Стрелка</h3>
              <Button label="Удалить стрелку" ruby ghost @click="removeEdge" />
            </div>
            <div class="flex flex-col gap-4">
              <Input
                v-model="selectedEdge.label"
                label="Подпись стрелки"
                :maxlength="300"
              /><label class="flex flex-col gap-2 text-sm"
                >Смысл связи<select
                  v-model="selectedEdge.data.kind"
                  :class="selectClass"
                  @change="
                    selectedEdge.style = {
                      stroke: 'rgb(var(--slate-9))',
                      strokeDasharray:
                        selectedEdge.data.kind === 'reference' ? '5 4' : 'none',
                    }
                  "
                >
                  <option value="transition">Следующий шаг</option>
                  <option value="reference">Пояснение</option>
                </select></label
              >
              <p class="break-all text-xs text-n-slate-11">
                {{ selectedEdge.source }} → {{ selectedEdge.target }}
              </p>
            </div>
          </template>
          <p v-else class="text-sm text-n-slate-11">
            Выберите блок или стрелку для редактирования.
          </p>
        </fieldset>
      </div>
      <details class="rounded-lg border border-n-weak p-4">
        <summary class="cursor-pointer text-sm font-medium">
          Стрелки · {{ doc.edges.length }} · добавить или выбрать без
          перетаскивания
        </summary>
        <fieldset
          :disabled="!editable"
          class="m-0 mt-4 grid min-w-0 gap-3 border-0 p-0 md:grid-cols-2"
        >
          <label class="flex flex-col gap-2 text-sm"
            >От блока<select v-model="linkFrom" :class="selectClass">
              <option value="">Выберите блок</option>
              <option v-for="n in doc.nodes" :key="n.id" :value="n.id">
                {{ n.data.title }}
              </option>
            </select></label
          ><label class="flex flex-col gap-2 text-sm"
            >К блоку<select v-model="linkTo" :class="selectClass">
              <option value="">Выберите блок</option>
              <option v-for="n in doc.nodes" :key="n.id" :value="n.id">
                {{ n.data.title }}
              </option>
            </select></label
          ><Input
            v-model="linkLabel"
            label="Подпись новой стрелки"
            :maxlength="300"
          /><Button
            label="Добавить стрелку"
            slate
            outline
            :disabled="!linkFrom || !linkTo || doc.edges.length >= 200"
            @click="
              connect({ source: linkFrom, target: linkTo, label: linkLabel })
            "
          />
        </fieldset>
        <div class="mt-4 flex flex-col gap-2">
          <Button
            v-for="e in doc.edges"
            :key="e.id"
            :label="`${doc.nodes.find(n => n.id === e.source)?.data.title} → ${doc.nodes.find(n => n.id === e.target)?.data.title}: ${e.label || 'Без подписи'}`"
            slate
            outline
            class="!h-auto !whitespace-normal !justify-start !text-left"
            @click="
              selectedEdgeId = e.id;
              selectedNodeId = '';
            "
          />
        </div>
      </details>
      <details class="rounded-lg border border-n-weak p-4">
        <summary class="cursor-pointer text-sm font-medium">
          Комментарии · {{ doc.comments.length }}
        </summary>
        <fieldset
          :disabled="!editable"
          class="m-0 mt-4 flex min-w-0 flex-col gap-3 border-0 p-0"
        >
          <label class="flex flex-col gap-2 text-sm"
            >Комментарий к<select v-model="commentNode" :class="selectClass">
              <option value="">Всей карте</option>
              <option v-for="n in doc.nodes" :key="n.id" :value="n.id">
                {{ n.data.title }}
              </option>
            </select></label
          ><TextArea
            :id="`${formId}-new-comment`"
            v-model="commentText"
            auto-height
            resize
            show-character-count
            min-height="5rem"
            max-height="24rem"
            label="Новый комментарий"
            :max-length="2000"
          /><Button
            label="Добавить комментарий"
            slate
            outline
            :disabled="!commentText.trim() || doc.comments.length >= 200"
            @click="addComment"
          />
          <div
            v-for="c in doc.comments"
            :key="c.id"
            class="flex flex-col gap-2 rounded-lg border border-n-weak p-3"
          >
            <span class="text-xs text-n-slate-11">{{
              c.node_id
                ? doc.nodes.find(n => n.id === c.node_id)?.data.title
                : 'Вся карта'
            }}</span
            ><TextArea
              :id="`${formId}-comment-${c.id}`"
              v-model="c.text"
              auto-height
              resize
              show-character-count
              min-height="5rem"
              max-height="24rem"
              label="Текст комментария"
              :max-length="2000"
              @input="changed"
            /><label class="flex gap-2 text-sm"
              ><input
                v-model="c.resolved"
                type="checkbox"
                @change="changed"
              />Вопрос решён</label
            ><Button
              label="Удалить комментарий"
              ruby
              ghost
              @click="
                remember();
                doc.comments = doc.comments.filter(v => v.id !== c.id);
                changed();
              "
            />
          </div>
        </fieldset>
      </details>
      <div v-if="!historical" class="flex flex-wrap items-end gap-3">
        <Input
          v-model="versionNote"
          label="Что изменилось в этой версии (необязательно)"
          :maxlength="300"
          class="min-w-0 flex-1"
          :disabled="busy || !!pending"
        /><Button
          label="Сохранить новую версию"
          :disabled="!editable || !hasChanges"
          :is-loading="busy"
          @click="save"
        /><Button
          v-if="dirty && error && !pending"
          label="Скачать несохранённый черновик"
          slate
          outline
          @click="
            download(
              JSON.stringify(
                {
                  planning_only: true,
                  draft: true,
                  document: ideaDocument(doc),
                },
                null,
                2
              ),
              'idea-map-draft.json',
              'application/json'
            )
          "
        />
      </div>
      <p class="text-xs text-n-slate-11">
        Сохраняйте изменения кнопкой «Сохранить новую версию». Для разбора
        используйте номер карты и версии либо скачайте задание.
      </p>
    </template>
  </div>
  <Dialog
    ref="confirmDialog"
    title="Подтверждение"
    confirm-button-label="Продолжить"
    @confirm="answerConfirm(true)"
    @close="answerConfirm(false, false)"
  >
    <p class="text-sm">{{ confirmText }}</p>
  </Dialog>
  <Dialog
    ref="createDialog"
    title="Новая карта идей"
    confirm-button-label="Создать карту"
    :disable-confirm-button="!newTitle.trim() || busy || !!pending"
    :is-loading="busy"
    @confirm="create"
  >
    <Input
      v-model="newTitle"
      label="Название новой карты"
      :disabled="busy || !!pending"
      :maxlength="160"
    /><Banner v-if="error" color="ruby" class="mt-3">
      {{ error }}
    </Banner>
  </Dialog>
  <Dialog
    ref="versionsDialog"
    title="Версии карты"
    :show-confirm-button="false"
  >
    <Banner v-if="error" color="ruby" class="mb-3">{{ error }}</Banner>
    <div class="flex max-h-[60vh] flex-col gap-3 overflow-y-auto">
      <div
        v-for="v in versions"
        :key="v.version"
        class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak p-3"
      >
        <div class="min-w-0 text-sm">
          <p>
            Версия {{ v.version }} ·
            {{ new Date(v.created_at).toLocaleString('ru-RU') }}
          </p>
          <p class="break-words text-n-slate-11">{{ v.note }}</p>
          <p v-if="v.source_version" class="text-xs text-n-slate-11">
            Восстановлена из версии {{ v.source_version }}
          </p>
        </div>
        <Button
          label="Открыть версию"
          slate
          outline
          :disabled="busy"
          @click="viewVersion(v.version)"
        />
      </div>
      <Button
        v-if="nextVersion"
        label="Ещё версии"
        slate
        outline
        :disabled="busy"
        @click="showVersions(true)"
      />
    </div>
  </Dialog>
  <Dialog
    ref="exportDialog"
    title="Подготовить карту для разбора"
    :show-confirm-button="false"
  >
    <p class="mb-4 text-sm">
      Карта №{{ mapId }}, версия {{ version }}. Группа: {{ group.title }}.
      Скачайте задание или напишите в чате: «Разбери карту №{{ mapId }} версии
      {{ version }}».
    </p>
    <p class="mb-4 text-sm text-n-slate-11">
      Снимок содержит все блоки, подписи стрелок и комментарии. Отдельного
      обращения к ИИ или запуска сценария не происходит.
    </p>
    <div class="flex flex-wrap gap-3">
      <Button
        label="Скачать задание (.md)"
        slate
        outline
        :disabled="busy"
        @click="downloadVersion('markdown')"
      /><Button
        label="Скачать JSON"
        slate
        outline
        :disabled="busy"
        @click="downloadVersion('json')"
      />
    </div>
    <Banner v-if="error" color="ruby" class="mt-3">{{ error }}</Banner>
  </Dialog>
</template>

<style scoped>
.idea-map-canvas {
  height: 600px;
}
</style>
